import type { components } from '@bookingdei/api-client/types';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatClock } from '@bookingdei/i18n/format-date';
import type { Lang } from '@bookingdei/i18n/translate';

type BusinessPage = components['schemas']['BusinessPage'];

// What the coded device mockups (app band, for-business showcase) print about the
// showcase business — read from its real business page, never typed in.
export interface ShowcaseProfile {
  name: string;
  cover: string | undefined;
  rating: number;
  reviews: number;
  area: string;
  hours: string | null;
  payKey: MessageKey | null;
  services: { name: string; minutes: number | undefined; price: number }[];
  serviceCount: number;
  staff: { name: string; photo: string | undefined }[];
}

const PAY_KEYS: Record<string, MessageKey> = {
  bkash: 'payment.bkash',
  nagad: 'payment.nagad',
  rocket: 'payment.rocket',
  bank: 'payment.bank',
  pay_at_venue: 'payment.pay_at_venue',
};

export function showcaseProfile(page: BusinessPage, lang: Lang, now: Date): ShowcaseProfile {
  const b = page.business;
  const today = b.hours?.find((h) => h.weekday === now.getDay());
  const clock = (min: number) => formatClock(lang, Math.floor(min / 60), min % 60);
  const hours =
    // null (the api's closed weekday, DB-11c) and absent both mean closed.
    today && today.open_min != null && today.close_min != null ? `${clock(today.open_min)}–${clock(today.close_min)}` : null;
  const pay = b.payment_methods?.find((p) => p.is_default) ?? b.payment_methods?.[0];
  const cover = b.media?.find((m) => m.kind === 'photo')?.url;
  return {
    name: lang === 'bn' && b.name_bn ? b.name_bn : b.name,
    cover: cover?.['800'] ?? cover?.['400'],
    rating: b.rating ?? 0,
    reviews: b.review_count ?? 0,
    area: b.area_name ?? '',
    hours,
    payKey: pay ? (PAY_KEYS[pay.method] ?? null) : null,
    services: page.catalog.slice(0, 2).map((c) => ({
      name: (lang === 'bn' ? c.name_bn : c.name_en) ?? c.name_en ?? '',
      minutes: c.duration_min,
      price: c.price_minor,
    })),
    serviceCount: page.catalog.length,
    staff: page.staff.slice(0, 4).map((s) => ({
      name: ((lang === 'bn' ? s.name_bn : s.name) ?? s.name).split(' ')[0] ?? '',
      photo: s.photo_url?.['400'] ?? s.photo_url?.['800'],
    })),
  };
}
